import React, { useState, useMemo, useCallback } from 'react';
import { useApiMode } from '../context/ApiModeContext';
import './ApiExplorer.css';

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const API_TABS = [
  { id: 'create', label: 'Create' },
  { id: 'update', label: 'Edit' },
  { id: 'get-all', label: 'Get All Bank Accounts' },
];

const getDefaultCreateFields = () => ({
  isoCountryCode: 'US',
  bankName: 'Bank of America',
  currencyCode: 'USD',
  bankAccountNumber: '12345678',
  routingNumber: '026009593',
  accountType: 'Checking',
  beneficiaryName: 'Beneficiary',
});

const getDefaultUpdateFields = () => ({
  id: 2761,
  isoCountryCode: 'US',
  bankName: 'Bank of America',
  currencyCode: 'USD',
  bankAccountNumber: '12345678',
  routingNumber: '026009593',
  accountType: 'Checking',
  beneficiaryName: 'NGLs',
});

const sampleResponse = {
  id: 2761,
  accountId: 940651,
  isoCountryCode: 'US',
  isoCurrencyCode: 'USD',
  bankName: 'Bank of America',
  beneficiaryName: 'NGLs',
  accountType: 'Checking',
  canRemove: true,
  shortIdentifierDomestic: '5678',
  verificationStatus: 'Unverified',
  status: 'Active',
  externalAccountStatus: 'NotConfigured',
};

const BankAccountApiExplorer = ({
  baseUrl: baseUrlProp,
  partners = [],
  onClose,
  onLogEntry,
}) => {
  const { apiBaseUrl: contextBaseUrl } = useApiMode();
  const baseUrl = baseUrlProp || contextBaseUrl;
  const [activeTab, setActiveTab] = useState('create');
  const [createJson, setCreateJson] = useState(() =>
    JSON.stringify(getDefaultCreateFields(), null, 2)
  );
  const [updateJson, setUpdateJson] = useState(() =>
    JSON.stringify(getDefaultUpdateFields(), null, 2)
  );
  const [getAllAccountId, setGetAllAccountId] = useState('940651');
  const [responseText, setResponseText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const base = baseUrl.replace(/\/$/, '');

  const updateBankAccountId = useMemo(() => {
    try {
      const u = JSON.parse(updateJson);
      return (u && u.id != null) ? String(u.id) : '2761';
    } catch {
      return '2761';
    }
  }, [updateJson]);

  const tabConfig = useMemo(() => {
    const accountId = getAllAccountId || '940651';
    return {
      create: {
        method: 'POST',
        endpoint: `${base}/api/v1/bank-accounts`,
        fullUrl: `${base}/api/v1/bank-accounts`,
      },
      update: {
        method: 'PUT',
        endpoint: `${base}/api/v1/bank-accounts/{id}`,
        fullUrl: `${base}/api/v1/bank-accounts/${updateBankAccountId}`,
      },
      'get-all': {
        method: 'GET',
        endpoint: `${base}/api/v1/bank-accounts?account_id={account_id}`,
        fullUrl: `${base}/api/v1/bank-accounts?account_id=${accountId}`,
      },
    };
  }, [base, getAllAccountId, updateBankAccountId]);

  const currentFullUrl = tabConfig[activeTab]?.fullUrl || '';

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  const partnerName = partners[0] ? partners[0].name : 'Unknown';

  const handleSend = async () => {
    setError('');
    setIsLoading(true);
    const partnerName = partners[0] ? partners[0].name : 'Connection';
    try {
      const base = baseUrl.replace(/\/$/, '');
      let url; let method; let body;
      if (activeTab === 'create') {
        url = `${base}/api/v1/bank-accounts`;
        method = 'POST';
        body = JSON.stringify(JSON.parse(createJson));
      } else if (activeTab === 'update') {
        url = `${base}/api/v1/bank-accounts/${updateBankAccountId}`;
        method = 'PUT';
        body = updateJson;
      } else {
        url = `${base}/api/v1/bank-accounts?account_id=${getAllAccountId}`;
        method = 'GET';
      }
      const opts = method === 'GET' ? {} : { headers: { 'Content-Type': 'application/json' }, body };
      const r = await fetch(url, { method, ...opts });
      const res = await r.json().catch(() => ({}));
      const responseStr = JSON.stringify(Array.isArray(res) ? res : res, null, 2);
      setResponseText(responseStr);
      const actionLabel = activeTab === 'create' ? 'Create Bank Account API' : activeTab === 'update' ? 'Update Bank Account API' : 'Get All Bank Accounts API';
      if (onLogEntry && partnerName) {
        onLogEntry(partnerName, { action: actionLabel, status: r.ok ? 'Success' : 'Failed', details: responseStr.slice(0, 80) + '...', amount: 0 });
      }
    } catch (e) {
      setError(e.message || 'Request failed');
      setResponseText('');
      if (onLogEntry && partnerName) {
        onLogEntry(partnerName, { action: 'Bank Account API', status: 'Failed', details: String(e.message), amount: 0 });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    if (activeTab === 'create') {
      setCreateJson(JSON.stringify(getDefaultCreateFields(), null, 2));
    } else if (activeTab === 'update') {
      setUpdateJson(JSON.stringify(getDefaultUpdateFields(), null, 2));
    }
    setResponseText('');
    setError('');
  };

  const requestSections = {
    create: {
      title: 'Request body',
      content: (
        <>
          <pre className="api-pre" style={{ marginBottom: 10, fontSize: 11 }}>
            {`Optional/country-specific: swiftBic, iban, sortCode, transitCode,
bankInstitutionNumber, branchCode, bankCode, bankIfscBranchCode,
pix (Brazil), bankAddress { isoCountryCode, line1, line2, province, state, postalCode, city }`}
          </pre>
          <textarea
            className="api-textarea"
            value={createJson}
            onChange={(e) => setCreateJson(e.target.value)}
            rows={14}
            placeholder={`{
  "isoCountryCode": "US",
  "bankName": "Bank of America",
  "currencyCode": "USD",
  "bankAccountNumber": "12345678",
  "routingNumber": "026009593",
  "swiftBic": "BOFAUS3NXXX",
  "accountType": "Checking",
  "beneficiaryName": "Beneficiary",
  "bankAddress": { "isoCountryCode": "US", "line1": "...", "city": "...", "postalCode": "..." }
}`}
          />
        </>
      ),
      showActions: true,
    },
    update: {
      title: 'Request body',
      content: (
        <textarea
          className="api-textarea"
          value={updateJson}
          onChange={(e) => setUpdateJson(e.target.value)}
          rows={14}
          placeholder='{ "id": 2761, "bankName": "...", "accountType": "Checking", ... }'
        />
      ),
      showActions: true,
    },
    'get-all': {
      title: 'Query parameters',
      content: (
        <div className="api-form">
          <div className="form-group">
            <label>account_id (optional)</label>
            <input
              type="text"
              className="form-input"
              value={getAllAccountId}
              onChange={(e) => setGetAllAccountId(e.target.value)}
              placeholder="940651"
            />
          </div>
          <pre className="api-pre">{`Filter by account ID. Omit to list all bank accounts.`}</pre>
        </div>
      ),
      showActions: true,
    },
  };

  const responseSections = {
    create: JSON.stringify(sampleResponse, null, 2),
    update: JSON.stringify(sampleResponse, null, 2),
    'get-all': JSON.stringify([sampleResponse, { ...sampleResponse, id: 2762, bankName: 'Chase' }], null, 2),
  };

  const errorSections = {
    create: [
      { code: '400', description: 'Bad Request – Invalid or missing required fields' },
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '422', description: 'Unprocessable Entity – Validation failed (e.g. invalid PIX, SWIFT)' },
      { code: '500', description: 'Internal Server Error' },
    ],
    update: [
      { code: '400', description: 'Bad Request – Invalid request body' },
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '404', description: 'Not Found – Bank account not found' },
      { code: '422', description: 'Unprocessable Entity – Validation failed' },
      { code: '500', description: 'Internal Server Error' },
    ],
    'get-all': [
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '403', description: 'Forbidden – No access to this account' },
      { code: '500', description: 'Internal Server Error' },
    ],
  };

  const reqSection = requestSections[activeTab];
  const resSection = responseSections[activeTab];
  const errSection = errorSections[activeTab];

  return (
    <div className="api-explorer api-explorer-sidebar">
      <div className="api-explorer-header">
        <h3>Bank Account API</h3>
        <button type="button" className="api-explorer-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="api-explorer-tabs">
        {API_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`api-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="api-explorer-panel">
        <div className="api-endpoint-block">
          <label className="api-endpoint-label">Base API endpoint</label>
          <div className="api-endpoint-value">{baseUrl}</div>
          <label className="api-endpoint-label">API endpoint</label>
          <div className="api-endpoint-row">
            <input type="text" className="api-endpoint-input" value={currentFullUrl} readOnly />
            <button
              type="button"
              className="api-endpoint-copy"
              onClick={() => copyToClipboard(currentFullUrl)}
              title="Copy endpoint"
              aria-label="Copy endpoint"
            >
              <CopyIcon />
            </button>
          </div>
          <div className="api-method-badge-inline">
            <span className="api-method-badge">{tabConfig[activeTab]?.method || 'GET'}</span>
          </div>
        </div>

        {reqSection && (
          <section className="api-doc-section">
            <h4>{reqSection.title}</h4>
            {reqSection.content}
            {reqSection.showActions && (
              <div className="api-tab-actions">
                <button type="button" className="btn btn-primary btn-sm" onClick={handleSend} disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleClear}>Clear</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
              </div>
            )}
          </section>
        )}

        <section className="api-doc-section">
          <h4>Response</h4>
          <pre className="api-pre api-response-pre">{resSection}</pre>
        </section>

        <section className="api-doc-section">
          <h4>Error</h4>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {errSection.map((row) => (
                <tr key={row.code}>
                  <td><code>{row.code}</code></td>
                  <td>{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      {reqSection?.showActions && (
        <div className="api-response-section">
          <h4>Live response</h4>
          {error && <div className="error-message">{error}</div>}
          <textarea
            className="api-textarea response-textarea"
            readOnly
            value={responseText || (isLoading ? 'Loading...' : 'Click Send to see response')}
            rows={6}
          />
        </div>
      )}
    </div>
  );
};

export default BankAccountApiExplorer;
